import { TextField, Typography } from "@mui/material";
import { SxProps } from "@mui/system";
import { DistaAutocomplete } from "@sista/dista-ui";
import { IdmClientContext } from "@sista/idm-client";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type OrganizationPickerEntity = {
  id: string;
  displayName: string;
  sortName?: string;
  dateDisabled?: string | null;
  officialAddress?: {
    state?: string;
    stateCode?: string;
  };
};

export type OrganizationPickerProps = {
  value?: string;
  onValueChange: (
    id: string | undefined,
    entity: OrganizationPickerEntity | undefined,
  ) => void;
  label?: string;
  sx?: SxProps;
};

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

// Vlastní obdoba SistaRejstrikPickerElement (entityType="organizations"), která
// navíc pod název organizace v nabídce zobrazuje zemi sídla monospace fontem.
export const OrganizationPicker = (props: OrganizationPickerProps) => {
  const { t } = useTranslation("ucet");
  const { loggedAccount } = useContext(IdmClientContext);

  const [internalValue, setInternalValue] = useState<
    OrganizationPickerEntity | undefined
  >(undefined);
  const [displayText, setDisplayText] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<OrganizationPickerEntity[]>([]);
  const previousController = useRef<AbortController>();

  useEffect(() => {
    if (props.value == null && internalValue != null) {
      setInternalValue(undefined);
      setDisplayText("");
    }
    // reagujeme jen na vnější zrušení hodnoty (zpětné nastavení z formuláře)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedQuery(displayText.trim()),
      DEBOUNCE_MS,
    );
    return () => clearTimeout(timeout);
  }, [displayText]);

  useEffect(() => {
    if (previousController.current) {
      previousController.current.abort();
    }

    if (debouncedQuery.length < MIN_QUERY_LENGTH) {
      setOptions([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    previousController.current = controller;
    setLoading(true);

    fetch(
      `${process.env.REACT_APP_REJSTRIK_ROOT}/api/v1/organizations/filter`,
      {
        method: "POST",
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${loggedAccount.authKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: debouncedQuery }),
      },
    )
      .then((r) => r.json())
      .then((data: OrganizationPickerEntity[]) => {
        const filtered = (data ?? []).filter((x) => x.dateDisabled == null);
        filtered.sort((a, b) =>
          (a.sortName ?? a.displayName).localeCompare(
            b.sortName ?? b.displayName,
          ),
        );
        setOptions(filtered);
        setLoading(false);
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          setOptions([]);
          setLoading(false);
        }
      });
  }, [debouncedQuery, loggedAccount.authKey]);

  return (
    <DistaAutocomplete
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.displayName
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      loading={loading}
      includeInputInList
      filterSelectedOptions
      multiple={false}
      value={internalValue ?? null}
      inputValue={displayText}
      noOptionsText={
        debouncedQuery.length >= MIN_QUERY_LENGTH
          ? t("ucet.organization.form.searchNoOptions", "Nenalezeno nic")
          : t(
              "ucet.organization.form.searchMinChars",
              "Zadejte alespoň 3 znaky...",
            )
      }
      onChange={(event, newValue) => {
        const entity =
          newValue && typeof newValue !== "string" ? newValue : undefined;
        setInternalValue(entity);
        setDisplayText(entity?.displayName ?? "");
        props.onValueChange(entity?.id, entity);
      }}
      onInputChange={(event, newInputValue) => setDisplayText(newInputValue)}
      label={props.label}
      sx={{ width: "100%", ...props.sx }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          InputLabelProps={{ shrink: Boolean(displayText) }}
        />
      )}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      renderOption={(liProps, option) => (
        <li {...liProps} key={option.id} style={{ display: "block" }}>
          <Typography variant="body2" color="text.primary">
            {option.displayName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {option.officialAddress?.state ??
              t("ucet.organization.form.countryNotFilled", "Neuvedeno")}
          </Typography>
        </li>
      )}
    />
  );
};
